import { useState, useEffect, useContext } from "react"
import { GlobalContextProvider } from "./functions/GlobalContext"
import {
    Box, Text, Center, Spinner, Button,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Stat, StatLabel, StatNumber,
    SimpleGrid, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Link,
} from "@chakra-ui/react"
import { ChevronRightIcon } from '@chakra-ui/icons'
import InfoModal from "./modals/InfoModal"
import Const from "../Consts"
import { Twirp, TwirpProps } from "./functions/Twirp"
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// level: 0=Country, 1=State, 2=City, 3=Cabang, 4=Ranting
const LEVEL_NAMES = ["Country", "State", "City", "Cabang/ Kecamatan", "Ranting"]

type OrgStat = {
    id: number
    name: string
    state_count: number
    city_count: number
    cabang_count: number
    ranting_count: number
    active_pesilat_count: number
    referee_count: number
}

export default function CountryStats() {
    const globalContext = useContext(GlobalContextProvider)
    const { t } = useTranslation();
    const navigate = useNavigate()

    const [level, setLevel] = useState(0)
    const [stats, setStats] = useState<OrgStat[]>([])
    const [loading, setLoading] = useState(true)

    // Drill-down context
    const [countryId, setCountryId] = useState(0)
    const [countryName, setCountryName] = useState("")
    const [stateId, setStateId] = useState(0)
    const [stateName, setStateName] = useState("")
    const [cityId, setCityId] = useState(0)
    const [cityName, setCityName] = useState("")
    const [cabangId, setCabangId] = useState(0)
    const [cabangName, setCabangName] = useState("")

    const [modalData, setModalData] = useState({ title: "", main: "", isOpen: false })
    const closeModal = () => setModalData({ ...modalData, isOpen: false })

    const token = globalContext?.GetLoginData().token

    const loadStats = async () => {
        setLoading(true)
        let parentId = 0
        if (level == 1) parentId = countryId
        else if (level == 2) parentId = stateId
        else if (level == 3) parentId = cityId
        else if (level == 4) parentId = cabangId

        const props: TwirpProps = {
            url: Const.API_ListCountryStats,
            headers: { Authorization: token },
            timeout: 10000,
            data: { level: level, parent_id: parentId }
        }
        const result = await Twirp(props)
        if (result.state == 2) {
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: result.axiosError.message })
        } else if (result.state == 1) {
            let msg = result.axiosError.response?.data?.msg || result.axiosError.message
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: msg })
            if (result.axiosError.response?.status == 401) {
                navigate('/logout')
            }
        } else {
            const raw = result.data.Stats || []
            setStats(raw.map((v: any) => ({
                id: +v.id,
                name: v.name,
                state_count: +v.state_count,
                city_count: +v.city_count,
                cabang_count: +v.cabang_count,
                ranting_count: +v.ranting_count,
                active_pesilat_count: +v.active_pesilat_count,
                referee_count: +v.referee_count,
            })))
        }
        setLoading(false)
    }

    useEffect(() => {
        loadStats()
    }, [level, countryId, stateId, cityId, cabangId])

    const drillDown = (s: OrgStat) => {
        if (level == 0) {
            setCountryId(s.id)
            setCountryName(s.name)
            setLevel(1)
        } else if (level == 1) {
            setStateId(s.id)
            setStateName(s.name)
            setLevel(2)
        } else if (level == 2) {
            setCityId(s.id)
            setCityName(s.name)
            setLevel(3)
        } else if (level == 3) {
            setCabangId(s.id)
            setCabangName(s.name)
            setLevel(4)
        }
    }

    const goBackTo = (targetLevel: number) => {
        setLevel(targetLevel)
    }

    const canDrillDown = true
    const currentLevelName = t(LEVEL_NAMES[level])

    const totals = stats.reduce((acc, s) => ({
        states: acc.states + s.state_count,
        cities: acc.cities + s.city_count,
        cabangs: acc.cabangs + s.cabang_count,
        rantings: acc.rantings + s.ranting_count,
        pesilats: acc.pesilats + s.active_pesilat_count,
        referees: acc.referees + s.referee_count,
    }), { states: 0, cities: 0, cabangs: 0, rantings: 0, pesilats: 0, referees: 0 })

    // Which count columns to show at this level
    const showStateCol = level == 0
    const showCityCol = level <= 1
    const showCabangCol = level <= 2
    const showRantingCol = level <= 3

    return (
        <Box py={"10px"} px={"10px"} borderWidth='0px' borderRadius='lg' overflow='hidden'>
            <InfoModal isOpen={modalData.isOpen} title={modalData.title} mainText={modalData.main} closeFunction={closeModal} />

            <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />} mb={"10px"}>
                <BreadcrumbItem>
                    <BreadcrumbLink onClick={() => goBackTo(0)} color={level == 0 ? "blue.500" : "gray.500"}>{t('Country')}</BreadcrumbLink>
                </BreadcrumbItem>
                {level >= 1 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(1)} color={level == 1 ? "blue.500" : "gray.500"}>{countryName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 2 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(2)} color={level == 2 ? "blue.500" : "gray.500"}>{stateName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 3 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(3)} color={level == 3 ? "blue.500" : "gray.500"}>{cityName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 4 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink color="blue.500">{cabangName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
            </Breadcrumb>

            <Text fontSize='md' fontWeight={'bold'} mb={"10px"}>{currentLevelName}</Text>

            {loading ? (
                <Center py={"40px"}><Spinner size='lg' /></Center>
            ) : stats.length === 0 ? (
                <Center py={"40px"}><Text fontSize='sm'>{t('no_records')}</Text></Center>
            ) : (
                <>
                    {/* Summary cards */}
                    <SimpleGrid columns={[2, 7]} spacing={4} mb={"20px"}>
                        <Stat bg="teal.50" p={3} borderRadius="md">
                            <StatLabel color="teal.600">{currentLevelName}</StatLabel>
                            <StatNumber>{stats.length}</StatNumber>
                        </Stat>
                        {showStateCol ? (
                            <Stat bg="green.50" p={3} borderRadius="md">
                                <StatLabel color="green.600">{t('State')}</StatLabel>
                                <StatNumber>{totals.states}</StatNumber>
                            </Stat>
                        ) : ''}
                        {showCityCol ? (
                            <Stat bg="purple.50" p={3} borderRadius="md">
                                <StatLabel color="purple.600">{t('City')}</StatLabel>
                                <StatNumber>{totals.cities}</StatNumber>
                            </Stat>
                        ) : ''}
                        {showCabangCol ? (
                            <Stat bg="orange.50" p={3} borderRadius="md">
                                <StatLabel color="orange.600">{t('Cabang/ Kecamatan')}</StatLabel>
                                <StatNumber>{totals.cabangs}</StatNumber>
                            </Stat>
                        ) : ''}
                        {showRantingCol ? (
                            <Stat bg="red.50" p={3} borderRadius="md">
                                <StatLabel color="red.600">{t('Ranting')}</StatLabel>
                                <StatNumber>{totals.rantings}</StatNumber>
                            </Stat>
                        ) : ''}
                        <Stat bg="blue.50" p={3} borderRadius="md">
                            <StatLabel color="blue.600">{t('Active Pesilat')}</StatLabel>
                            <StatNumber>{totals.pesilats}</StatNumber>
                        </Stat>
                        <Stat bg="purple.50" p={3} borderRadius="md">
                            <StatLabel color="purple.600">{t('Wasit/Juri')}</StatLabel>
                            <StatNumber>{totals.referees}</StatNumber>
                        </Stat>
                    </SimpleGrid>

                    {/* Detail table */}
                    <TableContainer>
                        <Table variant='simple' colorScheme='blue' size='sm'>
                            <Thead>
                                <Tr>
                                    <Th>{t('Name')}</Th>
                                    {showStateCol ? <Th isNumeric>{t('State')}</Th> : ''}
                                    {showCityCol ? <Th isNumeric>{t('City')}</Th> : ''}
                                    {showCabangCol ? <Th isNumeric>{t('Cabang/ Kecamatan')}</Th> : ''}
                                    {showRantingCol ? <Th isNumeric>{t('Ranting')}</Th> : ''}
                                    <Th isNumeric>{t('Active Pesilat')}</Th>
                                    <Th isNumeric>{t('Wasit/Juri')}</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {stats.map((s) => (
                                    <Tr key={s.id}>
                                        <Td fontWeight='bold'>
                                            {level == 4 ? (
                                                <Link href={"/rantingpesilat/" + s.id} target='_blank' rel='noreferrer' color='blue.500'>
                                                    {s.name}
                                                </Link>
                                            ) : (
                                                <Button variant='link' colorScheme='blue' size='sm' onClick={() => drillDown(s)}>
                                                    {s.name}
                                                </Button>
                                            )}
                                        </Td>
                                        {showStateCol ? <Td isNumeric>{s.state_count}</Td> : ''}
                                        {showCityCol ? <Td isNumeric>{s.city_count}</Td> : ''}
                                        {showCabangCol ? <Td isNumeric>{s.cabang_count}</Td> : ''}
                                        {showRantingCol ? <Td isNumeric>{s.ranting_count}</Td> : ''}
                                        <Td isNumeric color='green.500' fontWeight='bold'>{s.active_pesilat_count}</Td>
                                        <Td isNumeric color='purple.500' fontWeight='bold'>{s.referee_count}</Td>
                                    </Tr>
                                ))}
                                <Tr bg="gray.50" fontWeight='bold'>
                                    <Td>{t('Total')}</Td>
                                    {showStateCol ? <Td isNumeric>{totals.states}</Td> : ''}
                                    {showCityCol ? <Td isNumeric>{totals.cities}</Td> : ''}
                                    {showCabangCol ? <Td isNumeric>{totals.cabangs}</Td> : ''}
                                    {showRantingCol ? <Td isNumeric>{totals.rantings}</Td> : ''}
                                    <Td isNumeric color='green.500'>{totals.pesilats}</Td>
                                    <Td isNumeric color='purple.500'>{totals.referees}</Td>
                                </Tr>
                            </Tbody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    )
}
